import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import PageLayout from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Search, AlertCircle, EyeOff, Eye, User, Calendar, FileText, ExternalLink } from "lucide-react";
import { formatDataItaliana } from "@/utils/dateUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AttenzionatiPage({ user, logout }) {
  const [note, setNote] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchNote = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/attenzionati`, { withCredentials: true });
      setNote(res.data);
    } catch (e) {
      console.error("Error fetching attenzionati:", e);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNote();
  }, []);

  const handleNascondi = async (commessaId, notaId) => {
    try {
      await axios.put(`${API}/attenzionati/${commessaId}/${notaId}/nascondi`, {}, { withCredentials: true });
      toast.success("Nota rimossa dalla lista");
      fetchNote();
    } catch (e) {
      console.error("Error hiding nota:", e);
      toast.error("Errore nella rimozione della nota");
    }
  };

  const handleToggleLetta = async (commessaId, notaId, currentLetta) => {
    try {
      await axios.put(
        `${API}/commesse-pose/${commessaId}/note-attenzione/${notaId}`,
        { letta: !currentLetta },
        { withCredentials: true }
      );
      toast.success(currentLetta ? "Nota segnata come non letta" : "Nota segnata come letta");
      fetchNote();
    } catch (e) {
      console.error("Error toggling nota letta:", e);
      toast.error("Errore nell'aggiornamento della nota");
    }
  };

  // Filtra per ricerca
  const noteFiltrate = note.filter(n => {
    const searchLower = searchTerm.toLowerCase();
    return (
      n.cliente_nome.toLowerCase().includes(searchLower) ||
      n.cliente_cognome.toLowerCase().includes(searchLower) ||
      n.riassunto.toLowerCase().includes(searchLower)
    );
  });

  return (
    <PageLayout user={user} logout={logout} testId="attenzionati-page" className="overflow-y-auto">
      <div className="p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <AlertCircle className="w-6 h-6 text-orange-500" />
            <h1 className="text-xl lg:text-2xl font-bold text-slate-900">Attenzionati</h1>
            {note.length > 0 && (
              <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold">
                {note.length}
              </span>
            )}
          </div>
          <p className="text-slate-600 text-sm">
            Clienti con note di attenzione attive nelle commesse
          </p>
        </div>

        {/* Barra di ricerca */}
        <Card className="mb-4">
          <CardContent className="py-3">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per nome cliente o nota..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-9"
                data-testid="search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Contenuto */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : noteFiltrate.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">
                {searchTerm ? "Nessun risultato trovato" : "Nessuna nota di attenzione attiva"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="attenzionati-table">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left p-4 font-semibold text-slate-700">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          Cliente
                        </div>
                      </th>
                      <th className="text-left p-4 font-semibold text-slate-700">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          Data
                        </div>
                      </th>
                      <th className="text-left p-4 font-semibold text-slate-700">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Riassunto
                        </div>
                      </th>
                      <th className="text-center p-4 font-semibold text-slate-700 w-24">
                        Azioni
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {noteFiltrate.map((nota, index) => (
                      <tr 
                        key={`${nota.commessa_id}-${nota.nota_id}`}
                        className={`border-b hover:bg-slate-50 transition-colors ${
                          !nota.letta ? "bg-orange-50/50" : ""
                        }`}
                        data-testid={`attenzionati-row-${index}`}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Link 
                              to={`/commesse-pose/${nota.commessa_id}`}
                              className="font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                            >
                              {nota.cliente_nome} {nota.cliente_cognome}
                              <ExternalLink className="w-3 h-3" />
                            </Link>
                          </div>
                        </td>
                        <td className="p-4 text-slate-600">
                          {formatDataItaliana(nota.data_inserimento)}
                        </td>
                        <td className="p-4">
                          <p className="text-slate-700 text-sm line-clamp-2" title={nota.nota}>
                            {nota.riassunto}
                            {nota.nota && nota.nota.length > 140 && "..."}
                          </p>
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {/* Pulsante Letta/Non Letta */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleLetta(nota.commessa_id, nota.nota_id, nota.letta)}
                              className={nota.letta ? "text-slate-400 hover:text-slate-600" : "text-blue-500 hover:text-blue-700 hover:bg-blue-50"}
                              title={nota.letta ? "Segna come non letta" : "Segna come letta"}
                              data-testid={`toggle-letta-btn-${index}`}
                            >
                              {nota.letta ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                            
                            {/* Pulsante Nascondi */}
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-orange-500 hover:text-orange-700 hover:bg-orange-50"
                                  data-testid={`hide-btn-${index}`}
                                  title="Rimuovi dalla lista"
                                >
                                  <EyeOff className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Rimuovi dalla lista</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Vuoi rimuovere questa nota dalla lista Attenzionati?
                                    <br />
                                    <strong>La nota rimarrà visibile nella Tab Attenzione della commessa.</strong>
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annulla</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleNascondi(nota.commessa_id, nota.nota_id)}
                                    className="bg-orange-500 hover:bg-orange-600"
                                  >
                                    Rimuovi
                                  </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PageLayout>
  );
}
